import { AnnualReportBusinessModel } from '@modeso/aumico-lib-annualreport-fe';
import { AccountMappingAccountItem, AccountType, IAccountMappingSubFLI, ItemType } from '@modeso/types__aumico-lib-annualreport-be';
import { MenuItemList } from '../interfaces/menu-item-list.class';
import { Item } from '../interfaces/menu-item.class';
import { isAccount, isEmpty, isFli, isGroup, isHeader, isSubTotal, isSubfli } from '../type-guards/type-guards';

export class MenuItemCreator {
    createMenuItem(
        item: Item,
        businessModel: string,
        parentType?: ItemType,
        switchAccountItemState = true,
        isAddAccountDisabled = false,
        isHighestLevelParent = false,
        supportOperation = true,
        disableEditingAnnualProfitValue = false,
        isAutomaticTransferOfAnnualResultEnabled?: boolean
    ): MenuItemList {
        let menuItemList: MenuItemList = new MenuItemList();
        if (isHighestLevelParent && isGroup(item)) {
            menuItemList = this.highestLevelGroupMenuList();
        } else if (isSubTotal(item)) {
            menuItemList = this.subTotalMenuList(item, businessModel);
        } else if (isFli(item)) {
            menuItemList = this.fliMenuList(item, businessModel, isAddAccountDisabled);
        } else if (isSubfli(item)) {
            menuItemList = this.subFliMenuList(item, businessModel);
        } else if (isHeader(item)) {
            menuItemList = this.headerMenuList(businessModel);
        } else if (isEmpty(item)) {
            menuItemList = this.spaceMenuList(businessModel);
        } else if (isAccount(item)) {
            menuItemList = this.accountMenuList(item, switchAccountItemState, businessModel, parentType, supportOperation, disableEditingAnnualProfitValue, isAutomaticTransferOfAnnualResultEnabled);
        } else if (isGroup(item)) {
            menuItemList = this.groupMenuList(item, businessModel);
        }
        return menuItemList;
    }
    private subTotalMenuList(item: Item, businessModel: string): MenuItemList {
        const menuItemList = new MenuItemList();
        menuItemList.addEdit().addAccount().isDisabled().addGroup().isDisabled().addBalanceSwitchAccount().isDisabled();

        if (businessModel == AnnualReportBusinessModel.HGB) {
            menuItemList.addDuplicateAccount().isDisabled();
            menuItemList.toggleFliNumbering(item);
        }

        menuItemList.addInsertHeaderAbove().addInsertHeaderBelow().addInsertEmptyRowAbove().addInsertEmptyRowBelow();
        return menuItemList;
    }
    private groupMenuList(item: Item, businessModel: string): MenuItemList {
        const menuItemList = new MenuItemList();
        menuItemList
            .addEdit()
            .addAccount()
            .isDisabled()
            .addGroup()
            .addSubTotal()
            .addBalanceSwitchAccount()
            .isDisabled();

        if (businessModel == AnnualReportBusinessModel.HGB) {
            menuItemList.addDuplicateAccount().isDisabled();
            menuItemList.toggleFliNumbering(item);
        }

        menuItemList.addInsertHeaderAbove().addInsertHeaderBelow().addInsertEmptyRowAbove().addInsertEmptyRowBelow();

        return menuItemList;
    }
    private fliMenuList(item: Item, businessModel: string, isAddAccountDisabled: boolean): MenuItemList {
        const menuItemList = new MenuItemList();
        menuItemList
            .addEdit()
            .addAccount()
        if (isAddAccountDisabled === true) {
            menuItemList.isDisabled();
        }
        menuItemList.addGroup()
            .isDisabled()
            .addSubTotal()
            .isDisabled()
            .addBalanceSwitchAccount()
            .isDisabled();

        if (businessModel == AnnualReportBusinessModel.HGB) {
            menuItemList.addDuplicateAccount().isDisabled();
            menuItemList.toggleFliNumbering(item);
        }
        menuItemList.addInsertHeaderAbove().addInsertHeaderBelow().addInsertEmptyRowAbove().addInsertEmptyRowBelow();
        return menuItemList;
    }

    private subFliMenuList(item: Item, businessModel: string): MenuItemList {
        const menuItemList = new MenuItemList();
        menuItemList
            .addEdit()
            .addAccount()
            .addGroup()
            .isDisabled()
            .addSubTotal()
            .isDisabled()
            .addBalanceSwitchAccount()
            .isDisabled();

        if (businessModel == AnnualReportBusinessModel.HGB) {
            menuItemList.addDuplicateAccount().isDisabled();
            menuItemList.toggleFliNumbering(item);
            if ((item as IAccountMappingSubFLI).type == ItemType.OF_THAT_ITEM) {
                menuItemList.isDisabled();
            }
        }

        menuItemList.addInsertHeaderAbove().addInsertHeaderBelow().addInsertEmptyRowAbove().addInsertEmptyRowBelow();

        return menuItemList;
    }

    private accountMenuList(
        item: AccountMappingAccountItem,
        switchAccountItemState: boolean,
        businessModel: string,
        parentType: ItemType,
        supportOperation: boolean,
        disableEditingAnnualProfitValue: boolean,
        isAutomaticTransferOfAnnualResultEnabled?: boolean
    ): MenuItemList {
        const menuItemList = new MenuItemList();

        if (isAccount(item) && item.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING && isAutomaticTransferOfAnnualResultEnabled) {

            return menuItemList;
        }

        menuItemList.addEdit();

        if (isAccount(item) && item.type !== AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING) {
            if (!supportOperation) {
                // If not in support operation mode, disable account related actions
                // Otherwise enable editing in support operation mode
                if (isAccount(item) && item.isCloneAccount) {
                    menuItemList.isDisabled();
                }
                if (isAccount(item) && item.isSummarized) {
                    menuItemList.isDisabled();
                }
            }

            menuItemList
                .addAccount()
                .isDisabled()
                .addGroup()
                .isDisabled()
                .addSubTotal()
                .isDisabled()
                .addBalanceSwitchAccount()
            if (!switchAccountItemState || (isAccount(item) && (item.isMasterCloneAccount || item.isCloneAccount))) {
                menuItemList.isDisabled();
            }

            if (businessModel == AnnualReportBusinessModel.HGB) {
                if (parentType === ItemType.OF_THAT_ITEM) {
                    menuItemList.addDuplicateAccount();
                }
                if (isAccount(item) && (item.isMasterCloneAccount || item.isCloneAccount)) {
                    menuItemList.isDisabled();
                }

                menuItemList.toggleFliNumbering().isDisabled();
            }
        }


        if (parentType === ItemType.SUB_FLI || parentType === ItemType.OF_THAT_ITEM) {
            return menuItemList
                .addInsertHeaderAbove()
                .isDisabled()
                .addInsertHeaderBelow()
                .isDisabled()
                .addInsertEmptyRowAbove()
                .isDisabled()
                .addInsertEmptyRowBelow()
                .isDisabled();
        }

        return menuItemList
            .addInsertHeaderAbove()
            .addInsertHeaderBelow()
            .addInsertEmptyRowAbove()
            .addInsertEmptyRowBelow();
    }

    private headerMenuList(businessModel: string): MenuItemList {
        const menuItemList = new MenuItemList();
        menuItemList
            .addEdit()
            .isDisabled()
            .addAccount()
            .isDisabled()
            .addGroup()
            .isDisabled()
            .addSubTotal()
            .isDisabled()
            .addBalanceSwitchAccount()
            .isDisabled();

        if (businessModel == AnnualReportBusinessModel.HGB) {
            menuItemList.addDuplicateAccount().isDisabled();
            menuItemList.toggleFliNumbering().isDisabled();
        }
        menuItemList.addInsertHeaderAbove().addInsertHeaderBelow().addInsertEmptyRowAbove().addInsertEmptyRowBelow();
        return menuItemList;
    }
    private spaceMenuList(businessModel: string): MenuItemList {
        const menuItemList = new MenuItemList();
        menuItemList
            .addEdit()
            .isDisabled()
            .addAccount()
            .isDisabled()
            .addGroup()
            .isDisabled()
            .addSubTotal()
            .isDisabled()
            .addBalanceSwitchAccount()
            .isDisabled();

        if (businessModel == AnnualReportBusinessModel.HGB) {
            menuItemList.addDuplicateAccount().isDisabled();
            menuItemList.toggleFliNumbering().isDisabled();
        }

        menuItemList.addInsertHeaderAbove().addInsertHeaderBelow().addInsertEmptyRowAbove().addInsertEmptyRowBelow();
        return menuItemList;
    }

    private highestLevelGroupMenuList(): MenuItemList {
        const menuItemList = new MenuItemList();
        menuItemList.addEdit().addGroup().addSubTotal();
        return menuItemList;
    }
}
